import { useEffect, useState } from "react";
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from "@microsoft/signalr";

const hubUrl = import.meta.env.VITE_COM_HUB_URL;

export function useSignalR() {
  const [connection, setConnection] = useState<HubConnection | null>(null);
  const [connectionState, setConnectionState] = useState<HubConnectionState>(HubConnectionState.Disconnected);

  useEffect(() => {
    const hubConnection = new HubConnectionBuilder()
      .withUrl(hubUrl)
      .configureLogging(LogLevel.Information)
      .withAutomaticReconnect()
      .build();

    setConnection(hubConnection);
  }, []);

  const startConnection = async () => {
    if (connection?.state === HubConnectionState.Disconnected) {
      try {
        await connection.start();
        setConnectionState(HubConnectionState.Connected);
      } catch (err) {
        console.error("Error starting connection:", err);
      }
    }
  };

  const stopConnection = async () => {
    if (connection?.state !== HubConnectionState.Disconnected) {
      try {
        await connection?.stop();
        setConnectionState(HubConnectionState.Disconnected);
      } catch (err) {
        console.error("Error stopping connection:", err);
      }
    }
  };

  const sendLogoutRequest = async (accountId: string): Promise<void> => {
    if (connection?.state === HubConnectionState.Connected) {
      try {
        await connection.invoke("BroadcastLogout", accountId);
      } catch (error) {
        console.error("Error sending logout request:", error);
        throw error; // Rethrow if you want to propagate the error to the caller
      }
    }
  };

  const setLogoutResponseCallback = (callback: (accountId: string) => void): void => {
    if (connection) {
      connection.off("UserLoggedOut");
      connection.on("UserLoggedOut", callback);
    }
  };

  const isConnected = (): boolean => {
    return connectionState === HubConnectionState.Connected;
  };

  return {
    connection,
    connectionState,
    startConnection,
    stopConnection,
    sendLogoutRequest,
    setLogoutResponseCallback,
    isConnected,
  };
}
